// import React, { createContext, useState, useEffect } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import * as api from "../api/index";
// import {  getUnitMappings } from "../actions/unitActions"; // Import action to fetch mappings

// const StockContext = createContext();

// const StockProvider = ({ children }) => {
//   const [trackStock, setTrackStock] = useState(false);
//   const [stock, setStock] = useState([]);
//   const dispatch = useDispatch();
//   const mappings = useSelector((state) => state.units.mappings); // Get mappings from Redux

//   // Fetch unit mappings once when the provider mounts
//   useEffect(() => {
//     dispatch(getUnitMappings());
//   }, [dispatch]);

//   const getCurrentStockQty = async (itemId) => {
//     try {
//       const { data } = await api.getCurrentStockQuantity(itemId);
//       console.log(data);
//       const currentStock = parseFloat(data.currentStock);
//       return { currentStock };
//     } catch (error) {
//       console.log(error.response);
//       return { currentStock: 0 };
//     }
//   };

//   const getConvertedQuantity = (item, type = "sale", targetUnitId) => {
//     try {
//       const fromUnitId = item?.units?.[type]?.unit?._id;
//       const toUnitId = targetUnitId || item?.units?.stock?.unit?._id; // Default to stock unit
//       const mapping = mappings.find(
//         (m) => m.fromUnit.toString() === fromUnitId && m.toUnit.toString() === toUnitId
//       );
//       const convertedQuantity = mapping
//         ? item.quantity * mapping.conversionFactor
//         : item.quantity; // Fallback to raw quantity if no mapping
//       return convertedQuantity * (type === "sale" ? -1 : 1); // Negative for sales
//     } catch (error) {
//       console.log(error);
//       return 0;
//     }
//   };

//   const contextValue = {
//     stock,
//     trackStock,
//     setTrackStock,
//     getCurrentStockQty,
//     getConvertedQuantity,
//   };

//   return (
//     <StockContext.Provider value={contextValue}>
//       {children}
//     </StockContext.Provider>
//   );
// };

// export { StockContext, StockProvider };

import React, { createContext, useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as api from "../api/index";
import {  getUnitMappings } from "../actions/unitActions";

const StockContext = createContext();

const StockProvider = ({ children }) => {
  const [trackStock, setTrackStock] = useState(false);
  const [stock, setStock] = useState([]);
  const dispatch = useDispatch();
  const mappings = useSelector((state) => state.units.mappings);

  // StockProvider wraps the whole app once, at the very root (see
  // providers.js) - it never remounts on login/logout, only on a full page
  // reload. So the mount-time effect below (still needed for "already
  // logged in, page was refreshed") can't be the only way this fetches: a
  // login that navigates without reloading would never re-run it. Login.js
  // calls this refreshMappings function directly right after a successful
  // sign-in for exactly that case.
  const refreshMappings = useCallback(() => {
    dispatch(getUnitMappings());
  }, [dispatch]);

  useEffect(() => {
    // Without this check, /units/mappings was firing on every visit to the
    // public Login screen too, before anyone had signed in.
    const isLoggedIn = !!JSON.parse(localStorage.getItem("profile") || "null")?.token;
    if (isLoggedIn) refreshMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useCallback keeps this function's identity stable across StockProvider
  // re-renders. Without it, every consumer's `useEffect(..., [getCurrentStockQty])`
  // (Dashboard, LowStockPage, etc.) re-fires on every StockProvider render,
  // re-requesting /stocks/quantity/:itemId for every item all over again.
  const getCurrentStockQty = useCallback(async (itemId) => {
    try {
      const { data } = await api.getCurrentStockQuantity(itemId);
      const currentStock = parseFloat(data.currentStock);
      return { currentStock };
    } catch (error) {
      console.error(error.response);
      return { currentStock: 0 };
    }
  }, []);


  // const getConvertedQuantity = (item, type = "sale", targetUnitId, convert = true) => {
  //   try {
  //     const toUnitId = String(item?.units?.[type]?.unit?._id);
  //     const fromUnitId = String(targetUnitId || item?.units?.stock?.unit?._id);
  //     const mapping = mappings.find((m) => {
  //       const fromUnitStr = String(m.fromUnit._id || m.fromUnit);
  //       const toUnitStr = String(m.toUnit._id || m.toUnit);
  //       const match = fromUnitStr === toUnitId && toUnitStr === fromUnitId;
  //       console.log(`Checking mapping in getConvertedQuantity: ${fromUnitStr} -> ${toUnitStr}, Match: ${match}`);
  //       return match;
  //     });
  
  //     let convertedQuantity = item.quantity;
  //     console.log(item)
  //     if (convert && mapping) {
  //       convertedQuantity = item.quantity *(1 / mapping.conversionFactor);
  //       console.log(`Converted ${item.quantity} from ${fromUnitId} to ${toUnitId}, Factor: ${(1 / mapping.conversionFactor)}, Result: ${convertedQuantity}`);
  //       convertedQuantity *= (type === "sale" ? -1 : 1); // Negate only if converting
  //     } else {
  //       console.log(`No conversion applied: ${item.quantity} remains in ${fromUnitId}`);
  //     }
  
  //     return convertedQuantity;
  //   } catch (error) {
  //     console.log(error);
  //     return 0;
  //   }
  // };

  // useCallback (dep: mappings) so this stays stable across re-renders, same
  // reasoning as getCurrentStockQty above - it's a dependency of effects
  // elsewhere (e.g. Dashboard.js's stock valuation calculation).
  const getConvertedQuantity = useCallback((item, type = "sale", targetUnitId, convert = true) => {
    // Validate item structure
    if (!item?.units?.[type]?.unit?._id || !item?.units?.stock?.unit?._id) {
      console.error(`Invalid item structure for type ${type}:`, item);
      return 0;
    }

    const toUnitId = String(item.units[type].unit._id); // Sale unit ID
    const fromUnitId = String(targetUnitId || item.units.stock.unit._id); // Stock unit ID or target

    // If units match, return quantity (negate for sales)
    if (toUnitId === fromUnitId) {
      return type === "sale" ? -item.quantity : item.quantity;
    }

    // Find conversion mapping
    const mapping = mappings.find((m) => {
      const fromUnitStr = String(m.fromUnit._id || m.fromUnit);
      const toUnitStr = String(m.toUnit._id || m.toUnit);
      return fromUnitStr === toUnitId && toUnitStr === fromUnitId;
    });

    let convertedQuantity = item.quantity;
    if (convert && mapping) {
      convertedQuantity = item.quantity * (1 / mapping.conversionFactor);
    }

    // Sales should be stored as negative stock movement so they reduce inventory.
    convertedQuantity *= type === "sale" ? -1 : 1;

    return convertedQuantity;
  }, [mappings]);

  // Same reasoning: stable identity via useCallback (dep: mappings).
  const convertPrice = useCallback((item, fromUnitId, toUnitId) => {
    // Validate item structure
    if (!item?.price || !fromUnitId || !toUnitId) {
      console.error('Invalid item structure for price conversion:', item);
      return 0;
    }

    const sourceUnitId = String(fromUnitId);
    const targetUnitId = String(toUnitId);

    // If units match, return price unchanged
    if (sourceUnitId === targetUnitId) {
      return item.price;
    }

    // Find conversion mapping
    const mapping = mappings.find((m) => {
      const fromUnitStr = String(m.fromUnit._id || m.fromUnit);
      const toUnitStr = String(m.toUnit._id || m.toUnit);
      return fromUnitStr === sourceUnitId && toUnitStr === targetUnitId;
    });

    return mapping ? item.price * mapping.conversionFactor : item.price;
  }, [mappings]);
  // const getConvertedQuantity = (item, type = "sale", targetUnitId, convert = true) => {
  //   try {
  //     const toUnitId = String(item?.units?.[type]?.unit?._id); // Sale unit ID
  //     const fromUnitId = String(targetUnitId || item?.units?.stock?.unit?._id); // Stock unit ID or target unit
  
  //     // If sale unit matches stock unit (or target unit), return original quantity
  //     if (toUnitId === fromUnitId) {
  //       console.log(`No conversion needed: Sale unit ${toUnitId} matches stock unit ${fromUnitId} , Returning: ${item.quantity}`);
  //       return item.quantity; // Return original quantity without negation
  //     }
  
  //     // Find conversion mapping
  //     const mapping = mappings.find((m) => {
  //       const fromUnitStr = String(m.fromUnit._id || m.fromUnit);
  //       const toUnitStr = String(m.toUnit._id || m.toUnit);
  //       const match = fromUnitStr === toUnitId && toUnitStr === fromUnitId;
  //       console.log(`Checking mapping: ${fromUnitStr} -> ${toUnitStr}, Match: ${match}`);
  //       return match;
  //     });
  
  //     let convertedQuantity = item.quantity;
  //     if (convert && mapping) {
  //       convertedQuantity = item.quantity * (1 / mapping.conversionFactor);
  //       console.log(`Converted ${item.quantity} from ${toUnitId} to ${fromUnitId}, Factor: ${1 / mapping.conversionFactor}, Result: ${convertedQuantity}`);
  //       convertedQuantity *= (type === "sale" ? -1 : 1); // Negate only if converting
  //     } else {
  //       console.log(`No conversion applied: ${item.quantity} remains in ${toUnitId}`);
  //     }
  
  //     return convertedQuantity;
  //   } catch (error) {
  //     console.log(error);
  //     return 0;
  //   }
  // };
  // getCurrentStockQty/getConvertedQuantity/convertPrice are already
  // useCallback-stable, so this only produces a new object (re-rendering
  // every consumer) when stock/trackStock actually change.
  const contextValue = useMemo(
    () => ({
      stock,
      trackStock,
      setTrackStock,
      getCurrentStockQty,
      getConvertedQuantity,
      convertPrice,
      refreshMappings,
    }),
    [stock, trackStock, getCurrentStockQty, getConvertedQuantity, convertPrice, refreshMappings]
  );

  return (
    <StockContext.Provider value={contextValue}>
      {children}
    </StockContext.Provider>
  );
};

export { StockContext, StockProvider };
